/**
 * Player API client.
 * Reads device_id + activation_key from localStorage, sends them as headers
 * on every authenticated /api/player/* call.
 */
import axios from "axios";
import { getDeviceId, detectPlatform } from "./deviceId";
import { APP_VERSION } from "./version";

// The bundled Android APK (and Electron Windows app) ALWAYS talks to the
// live admin panel at https://zaplayerpro.club — that's where the licensing
// + playlist + device records live. The dev preview URL in
// REACT_APP_BACKEND_URL is for the in-browser admin UI only, never for the
// shipped clients. We detect "native platform" at runtime via Capacitor
// instead of relying on a build-time env var so a `yarn build` of the
// Android target doesn't need extra flags.
const PRODUCTION_BACKEND_URL = "https://zaplayerpro.club";

function resolveBackendUrl() {
  // Runtime detection: Capacitor.isNativePlatform() is true inside the
  // packaged Android APK (and any future iOS build).
  if (
    typeof window !== "undefined" &&
    window.Capacitor &&
    typeof window.Capacitor.isNativePlatform === "function" &&
    window.Capacitor.isNativePlatform()
  ) {
    return PRODUCTION_BACKEND_URL;
  }
  // Electron desktop build also pins to the production admin host.
  // The preload script exposes `window.electronAPI.isElectron` via
  // contextBridge — `window.process` is NOT available in the renderer
  // when contextIsolation is on (the default since Electron 12), so we
  // can't sniff `process.versions.electron` from here.
  if (typeof window !== "undefined" && window.electronAPI?.isElectron) {
    return PRODUCTION_BACKEND_URL;
  }
  // Build-time override (kept for backwards compat with the old
  // REACT_APP_PROD_BACKEND_URL=… APK build flag), falling back to the
  // preview URL for in-browser dev.
  return (
    process.env.REACT_APP_PROD_BACKEND_URL || process.env.REACT_APP_BACKEND_URL
  );
}

const BACKEND_URL = resolveBackendUrl();
export const API = `${BACKEND_URL}/api`;
export const API_BASE = BACKEND_URL;

const ACTIVATION_KEY_STORAGE = "vupp_activation_key";

export function getStoredActivationKey() {
  return localStorage.getItem(ACTIVATION_KEY_STORAGE);
}
export function setStoredActivationKey(key) {
  localStorage.setItem(ACTIVATION_KEY_STORAGE, key);
}
export function clearActivation() {
  localStorage.removeItem(ACTIVATION_KEY_STORAGE);
}

export const playerAxios = axios.create({
  baseURL: API,
  timeout: 30000,
});

playerAxios.interceptors.request.use((config) => {
  const did = getDeviceId();
  const key = getStoredActivationKey();
  if (did) config.headers["X-Device-ID"] = did;
  if (key) config.headers["X-Activation-Key"] = key;
  config.headers["X-Platform"] = detectPlatform();
  config.headers["X-App-Version"] = APP_VERSION;
  return config;
});

// v1.4.1 — Mid-session 401 handler.
//
// When the admin deletes the current device from the Devices panel,
// EVERY authenticated player API call starts returning 401 "Invalid
// device credentials" from `get_authenticated_device`. Previously the
// app just showed a red toast and kept playing from the IDB cache —
// so the user thought "my delete didn't take effect" while the app
// actually WAS locked out but hidden behind the offline cache.
//
// New behaviour: on the first 401 (or 403 "device is blocked") from
// any player endpoint, we broadcast a `vupp:device-revoked` event.
// `DeviceContext` listens for this and forces the user back to the
// activation screen with the stored credentials wiped from both
// localStorage AND Electron's safeStorage. Guarantees admin deletes
// take effect within one request round-trip, not on next app boot.
let _revokedBroadcast = false;
playerAxios.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err?.response?.status;
    const url = err?.config?.url || "";
    // Only trip on player-scoped endpoints; the admin panel has its
    // own axios client with its own error semantics.
    const isPlayerEndpoint =
      url.includes("/player/") || url.startsWith("/auth/") || url.includes("/auth/");
    if ((status === 401 || status === 403) && isPlayerEndpoint && !_revokedBroadcast) {
      _revokedBroadcast = true;
      try {
        window.dispatchEvent(
          new CustomEvent("vupp:device-revoked", { detail: { status, url } }),
        );
      } catch {
        /* ignore */
      }
      // Reset after 5s so a legitimate transient 401 doesn't lock
      // the broadcast forever if the user then re-activates.
      setTimeout(() => {
        _revokedBroadcast = false;
      }, 5000);
    }
    return Promise.reject(err);
  },
);

// === Activation ===
export const activateDevice = (activationKey) =>
  axios
    .post(`${API}/auth/activate`, {
      device_id: getDeviceId(),
      activation_key: activationKey,
      platform: detectPlatform(),
      platform_version: navigator.platform || "unknown",
      app_version: APP_VERSION,
      device_name: "VU Player",
      device_model: "Desktop",
    })
    .then((r) => r.data);

export const validateDevice = () =>
  axios
    .post(
      `${API}/auth/validate`,
      {
        device_id: getDeviceId(),
        activation_key: getStoredActivationKey(),
      },
      {
        // v1.4.4 — Send the current runtime metadata so `/auth/validate`
        // can refresh the device row in the admin panel. Without these
        // headers, upgrading the app from (say) v1.4.0 → v1.4.4 leaves
        // the admin Devices page showing the OLD version forever.
        headers: {
          "X-App-Version": APP_VERSION,
          "X-Platform": detectPlatform(),
        },
      }
    )
    .then((r) => r.data);

export const playerMe = () => playerAxios.get("/auth/me").then((r) => r.data);

// === Branding (platform-aware) ===
export const getPlayerBranding = () =>
  playerAxios.get("/player/branding").then((r) => r.data);

// === Public panel info (panel name even pre-activation) ===
export const getPublicPanelInfo = () =>
  axios.get(`${API}/public/panel-info`).then((r) => r.data);

// === Playlists ===
// Note: content (channels/movies/series) is now parsed client-side in the player.
// The backend only returns playlist metadata; see /app/frontend/src/player/lib/playlistCache.js
// for the fetch + parse flow.
export const getAssignedPlaylists = () =>
  playerAxios.get("/player/playlists").then(async (r) => {
    // v1.3.41 — react to the admin's per-playlist / per-key Debug-mode
    // toggle. The server returns `debug_mode: true|false` as the OR of
    // the activation key's flag and every assigned playlist's flag.
    // We flip the local debug console accordingly so admins can
    // remote-collect logs without asking the user to type a magic key.
    try {
      if (typeof r.data?.debug_mode === "boolean") {
        const dbg = await import("./debugMode");
        if (r.data.debug_mode && !dbg.isDebugEnabled()) dbg.enableDebug();
        else if (!r.data.debug_mode && dbg.isDebugEnabled()) dbg.disableDebug();
      }
    } catch {/* never block playlist load on this */}
    return r.data;
  });

// Fire-and-forget cover harvest for the given playlist.
// The backend runs the harvest in a background task and 202s immediately —
// covers already on disk are skipped (refresh=false), so a cold app launch
// becomes a cheap delta walk after the first full pass.
export const triggerCoverHarvest = (playlistId) =>
  playerAxios
    .post(`/player/covers/harvest/${encodeURIComponent(playlistId)}`)
    .then((r) => r.data)
    .catch(() => null);

// v1.3.44 — Fetch the server-side Cover & Metadata Harvest snapshot.
// Returns one of three values:
//   * an object → snapshot body to merge.
//   * "disabled" → admin flipped b2c_enabled=false on this playlist
//     (HTTP 204). Caller must skip the overlay entirely, NOT fall back
//     to client-side B2C.
//   * null → no snapshot harvested yet (HTTP 404) OR network error.
//     Caller may fall back to client-side overlay.
export const FETCH_METADATA_DISABLED = "disabled";
export const fetchMetadataSnapshot = (playlistId) =>
  playerAxios
    .get(`/player/playlists/${encodeURIComponent(playlistId)}/metadata-snapshot`, {
      // Snapshots can be several MB; let axios stream them as text and
      // parse explicitly to keep memory predictable on TV WebViews.
      transformResponse: [(data) => data],
      // We want to inspect 204 / 404 in the success path; let axios
      // surface them rather than throw.
      validateStatus: (s) => s >= 200 && s < 500,
    })
    .then((r) => {
      if (r.status === 204) return FETCH_METADATA_DISABLED;
      if (r.status === 404) return null;
      if (r.status >= 400) return null;
      if (!r.data) return null;
      try {
        return typeof r.data === "string" ? JSON.parse(r.data) : r.data;
      } catch {
        return null;
      }
    })
    .catch(() => null);

// v1.3.50 — Fetch the server-side Playlist Snapshot.
// Storage format changed from "pre-parsed JSON blob" → "raw gzipped
// M3U". The new server endpoint serves the cached M3U with
// Content-Encoding: gzip so the WebView decompresses on the fly and
// returns a Response we can hand straight to the existing streaming
// parser (constant-memory, never OOMs even on 590k catalogs).
//
// Returns one of:
//   * A Response object on HIT — caller passes it to parseM3uStream.
//   * FETCH_METADATA_DISABLED — admin turned the toggle OFF (204).
//   * null — no snapshot yet (404) or network error.
// Result discriminator constants — caller checks identity rather than
// shape so 401 / 403 / 404 / network errors are distinguishable in logs
// even though they all mean "no snapshot for this device, fall back".
export const SNAPSHOT_NOT_FOUND = "snapshot-not-found";       // 404
export const SNAPSHOT_FORBIDDEN = "snapshot-forbidden";       // 401 / 403
export const SNAPSHOT_NETWORK_ERROR = "snapshot-network-error"; // fetch threw
export const fetchPlaylistSnapshot = async (playlistId) => {
  const url = `${API}/player/playlists/${encodeURIComponent(playlistId)}/playlist-snapshot`;
  try {
    // v1.3.62 — assemble auth headers inline (mirrors the
    // playerAxios.interceptors.request handler above). The previous
    // build referenced a `playerAuthHeaders()` helper that never
    // existed, which threw `playerAuthHeaders is not defined` on every
    // device → every snapshot fetch failed and silently fell back to
    // the streaming provider parse.
    const headers = {
      "X-Platform": detectPlatform(),
      "X-App-Version": APP_VERSION,
    };
    const did = getDeviceId();
    const key = getStoredActivationKey();
    if (did) headers["X-Device-ID"] = did;
    if (key) headers["X-Activation-Key"] = key;
    const resp = await fetch(url, { method: "GET", headers, cache: "no-store" });
    if (resp.status === 204) return FETCH_METADATA_DISABLED;
    if (resp.status === 404) return { __miss: SNAPSHOT_NOT_FOUND, status: 404, url };
    if (resp.status === 401 || resp.status === 403) {
      return { __miss: SNAPSHOT_FORBIDDEN, status: resp.status, url };
    }
    if (!resp.ok) return { __miss: SNAPSHOT_NOT_FOUND, status: resp.status, url };
    return resp;
  } catch (e) {
    return { __miss: SNAPSHOT_NETWORK_ERROR, error: String(e?.message || e), url };
  }
};

// === Notifications ===
export const getPlayerNotifications = () =>
  playerAxios.get("/player/notifications").then((r) => r.data);
export const getUnreadNotificationsCount = () =>
  playerAxios.get("/player/notifications/unread-count").then((r) => r.data);
export const markNotificationRead = (id) =>
  playerAxios.put(`/player/notifications/${id}/read`).then((r) => r.data);
export const markAllNotificationsRead = () =>
  playerAxios.put("/player/notifications/mark-all-read").then((r) => r.data);

// === Updates ===
export const checkForUpdates = () =>
  playerAxios.get("/player/updates/check").then((r) => r.data);

// === Profiles (synced per playlist) ===
export const listProfilesApi = (playlistId) =>
  playerAxios
    .get("/player/profiles", { params: playlistId ? { playlist_id: playlistId } : {} })
    .then((r) => r.data.profiles || []);

export const createProfileApi = (data) =>
  playerAxios.post("/player/profiles", data).then((r) => r.data);

export const updateProfileApi = (id, data) =>
  playerAxios.put(`/player/profiles/${id}`, data).then((r) => r.data);

export const deleteProfileApi = (id) =>
  playerAxios.delete(`/player/profiles/${id}`).then((r) => r.data);

export const verifyProfilePinApi = (id, pin) =>
  playerAxios.post(`/player/profiles/${id}/verify-pin`, { pin }).then((r) => r.data);

// v1.3.72 — profile concurrency lock. Throws AxiosError with
// response.status === 409 and response.data.detail when another
// device holds an active lease.
export const claimProfileApi = (id) =>
  playerAxios.post(`/player/profiles/${id}/claim`).then((r) => r.data);

export const releaseProfileApi = (id) =>
  playerAxios.post(`/player/profiles/${id}/release`).then((r) => r.data);

// Helper to get full asset URL for branding etc.
export const getAssetUrl = (relativePath) => {
  if (!relativePath) return null;
  if (relativePath.startsWith("http")) return relativePath;
  return `${BACKEND_URL}${relativePath}`;
};

// ===== TMDB metadata proxy =====
// Backend forwards these to themoviedb.org using the admin-configured API
// key. Player never sees the key. Returns null on any failure (no key set,
// 404, network) so callers can gracefully fall through to the provider's
// own data.
export async function fetchTmdbMovie(tmdbId) {
  if (!tmdbId) return null;
  try {
    const r = await playerAxios.get(`/tmdb/movie/${tmdbId}`);
    return r.data || null;
  } catch {
    return null;
  }
}

export async function fetchTmdbTv(tmdbId) {
  if (!tmdbId) return null;
  try {
    const r = await playerAxios.get(`/tmdb/tv/${tmdbId}`);
    return r.data || null;
  } catch {
    return null;
  }
}

/**
 * v1.1.1 — Fetch one random YouTube trailer key for a popular movie
 * released in `year`. Used by the Movie Cue intermission pre-roll
 * (2 current-year + 1 older trailer before each queued movie).
 * Returns `{ yt_key, title, year, tmdb_id }` or null.
 */
export async function fetchRandomTrailer(year) {
  if (!year) return null;
  try {
    const r = await playerAxios.get(`/tmdb/trailers/random`, { params: { year } });
    const d = r.data;
    if (!d || d.ok === false) return null;
    return d;
  } catch {
    return null;
  }
}

// Name+year fallback search — used when a source (e.g. 1-Stream B2C)
// doesn't expose a tmdb_id and we still want the gorgeous TMDB poster
// + backdrop in the drill-down view. Server-side proxy hits TMDB's
// `/search/tv` and unwraps the first hit, so the response shape is
// identical to `fetchTmdbTv()` (poster, backdrop, plot, …).
export async function fetchTmdbTvByName(name, year) {
  if (!name) return null;
  try {
    const r = await playerAxios.get(`/tmdb/search/tv`, {
      params: { q: name, year: year || undefined },
    });
    const d = r.data;
    if (!d || (Array.isArray(d.results) && d.results.length === 0)) return null;
    // Community contribute — when the server resolved via TMDB search or
    // IMDB scraper (i.e. NOT already in the resolver cache), push the
    // mapping back so the next device asking the same `(name, year)`
    // gets an instant resolver hit and we collectively starve our TMDB
    // rate-limit cost.
    contributeResolver("tv", name, year, d.tmdb_id, d._resolver?.source);
    return d;
  } catch {
    return null;
  }
}

export async function fetchTmdbMovieByName(name, year) {
  if (!name) return null;
  try {
    const r = await playerAxios.get(`/tmdb/search/movie`, {
      params: { q: name, year: year || undefined },
    });
    const d = r.data;
    if (!d || (Array.isArray(d.results) && d.results.length === 0)) return null;
    contributeResolver("movie", name, year, d.tmdb_id, d._resolver?.source);
    return d;
  } catch {
    return null;
  }
}

// Fire-and-forget POST to the community resolver. Idempotent server-side
// so repeat calls just bump the `confirmations` counter — no harm. We
// skip the call when the server already served us from the resolver
// (saves a needless round-trip) and when `tmdb_id` is missing.
function contributeResolver(kind, name, year, tmdbId, serverSource) {
  if (!tmdbId) return;
  // Server tags resolver hits with `_resolver.source` — if it came from
  // the resolver, nothing for us to contribute.
  if (serverSource && serverSource !== "tmdb-search" && serverSource !== "imdb-scraper") {
    return;
  }
  // Don't await — keep the metadata fetch path fast.
  playerAxios
    .post(`/metadata/contribute`, { kind, name, year: year || null, tmdb_id: tmdbId, source: "device-search" })
    .catch(() => { /* ignored — contribute is best-effort */ });
}
